import { useId, useState } from 'react'
import { ErrorNote } from './Feedback'
import { Select } from './Select'
import { MADS_CODE_PREFIXES, splitMadsCode, type MadsCodePrefix } from '@/lib/madsCode'
import { useSuggestMadsCode } from '@/lib/queries'
import { useIsOnline } from '@/lib/connectivity'

interface MadsCodeFieldProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

/** A numeric keyboard for routine labels, with free entry for existing exceptions (issue #74). */
export function MadsCodeField({ value, onChange, disabled = false }: MadsCodeFieldProps) {
  const id = useId()
  const initial = splitMadsCode(value)
  const [mode, setMode] = useState<'prefix' | 'free'>(value === '' || initial !== null ? 'prefix' : 'free')
  const [prefix, setPrefix] = useState<MadsCodePrefix>(initial?.prefix ?? 'MADS')
  const suggestion = useSuggestMadsCode()
  const online = useIsOnline()
  const parts = splitMadsCode(value)
  const busy = disabled || suggestion.isPending

  function changeMode(next: 'prefix' | 'free') {
    suggestion.reset()
    if (next === 'prefix' && value !== '' && parts === null) {
      // An unusual legacy code stays intact until the user explicitly replaces it.
      return
    }
    if (parts !== null) setPrefix(parts.prefix)
    setMode(next)
  }

  return (
    <div className="field">
      <label htmlFor={`${id}-code`}>MADS code</label>
      <div className="seg" role="radiogroup" aria-label="MADS code entry mode">
        <label className="seg-opt">
          <input type="radio" name={`${id}-mode`} checked={mode === 'prefix'}
            disabled={busy || (value !== '' && parts === null)} onChange={() => changeMode('prefix')} />
          Prefix
        </label>
        <label className="seg-opt">
          <input type="radio" name={`${id}-mode`} checked={mode === 'free'}
            disabled={busy} onChange={() => changeMode('free')} />
          Free entry
        </label>
      </div>
      {mode === 'prefix' ? (
        <div className="action-row">
          <Select aria-label="MADS code prefix" value={parts?.prefix ?? prefix}
            options={MADS_CODE_PREFIXES.map((entry) => ({ value: entry, label: entry }))}
            isDisabled={busy} onChange={(next) => {
              setPrefix(next as MadsCodePrefix)
              if (parts !== null) onChange(`${next}${parts.number}`)
            }} />
          <input id={`${id}-code`} className="input" style={{ flex: '1 1 120px', width: 'auto' }}
            type="text" inputMode="numeric" pattern="[0-9]*" placeholder="e.g. 001"
            value={parts?.number ?? ''} disabled={busy} onChange={(event) => {
              const number = event.target.value
              if (!/^[0-9]*$/.test(number)) return
              onChange(number === '' ? '' : `${parts?.prefix ?? prefix}${number}`)
            }} />
        </div>
      ) : (
        <>
          <input id={`${id}-code`} className="input" value={value} disabled={busy}
            onChange={(event) => onChange(event.target.value)} />
          {value !== '' && parts === null && (
            <p className="text-muted">Clear the code to use prefix mode.</p>
          )}
        </>
      )}
      <button type="button" className="btn btn-secondary" disabled={busy || !online}
        style={{ marginTop: 'var(--space-2)' }} onClick={() => {
          suggestion.mutate(undefined, { onSuccess: (code) => {
            setPrefix('MADS')
            setMode('prefix')
            onChange(code)
          } })
        }}>
        {suggestion.isPending ? 'Finding code…' : 'Use next MADS code'}
      </button>
      {!online && <p className="text-muted">Connect to find an unused code.</p>}
      <ErrorNote error={suggestion.error} />
    </div>
  )
}
